import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordPage() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) {
            toast.error("New passwords don't match");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated");
            await refresh?.();
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#050505]">
            <div className="w-full max-w-md mx-auto px-6">
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-7" data-testid="force-change-pw">
                    <div className="flex items-center gap-2 mb-1 text-[#7C3AED]">
                        <Shield className="w-3.5 h-3.5" strokeWidth={1.8} />
                        <span className="label-xs">Mandatory password change</span>
                    </div>
                    <h1 className="text-2xl font-display tracking-tight mb-2">
                        Update your password
                    </h1>
                    <p className="text-xs text-white/50 mb-6">
                        Your account ({user?.email}) is using a temporary password.
                        Please set a permanent one before continuing.
                    </p>
                    <form onSubmit={submit} className="space-y-4">
                        <Field label="Current (temporary) password" type="password" value={current} onChange={setCurrent} testId="cpw-current" />
                        <Field label="New password" type="password" value={next} onChange={setNext} testId="cpw-new"
                            hint="Min 12 chars · upper + lower + digit + symbol" />
                        <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} testId="cpw-confirm" />
                        <button type="submit" disabled={loading} data-testid="cpw-submit"
                            className="w-full bg-[#7C3AED] text-white font-medium text-sm py-2.5 rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-60 flex items-center justify-center gap-2">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {loading ? "Updating…" : "Update password"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ label, type = "text", value, onChange, hint, testId }) {
    return (
        <div>
            <label className="label-xs block mb-2">{label}</label>
            <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white/30" />
            {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
        </div>
    );
}
